import { useEffect, useRef, useState } from 'react';
import { loadGoogleMaps } from '../../utils/googleMapsLoader';

// Saudi Arabia bounds
const SAUDI_BOUNDS = {
  north: 32.5,
  south: 16.0,
  east: 56.0,
  west: 34.5,
};

export interface AddressComponents {
  streetAddress: string;
  city: string;
  region: string;
  district: string;
  postalCode: string;
  latitude: number;
  longitude: number;
  formattedAddress: string;
}

interface AddressAutocompleteProps {
  onPlaceSelect: (address: AddressComponents) => void;
  placeholder?: string;
  className?: string;
  error?: string;
  disabled?: boolean;
  initialValue?: string;
}

// Extract address components from Google Place result
function extractAddressComponents(place: google.maps.places.PlaceResult): AddressComponents {
  const components: AddressComponents = {
    streetAddress: '',
    city: '',
    region: '',
    district: '',
    postalCode: '',
    latitude: 0,
    longitude: 0,
    formattedAddress: place.formatted_address || '',
  };

  if (place.geometry?.location) {
    components.latitude = place.geometry.location.lat();
    components.longitude = place.geometry.location.lng();
  }

  const addressComponents = place.address_components || [];
  let streetNumber = '';
  let route = '';

  for (const component of addressComponents) {
    const types = component.types;

    if (types.includes('street_number')) {
      streetNumber = component.long_name;
    } else if (types.includes('route')) {
      route = component.long_name;
    } else if (types.includes('administrative_area_level_1')) {
      components.region = component.long_name;
    } else if (types.includes('locality')) {
      components.city = component.long_name;
    } else if (types.includes('administrative_area_level_2') && !components.city) {
      components.city = component.long_name;
    } else if (types.includes('sublocality_level_1')) {
      components.district = component.long_name;
    } else if (types.includes('sublocality') && !components.district) {
      components.district = component.long_name;
    } else if (types.includes('neighborhood') && !components.district) {
      components.district = component.long_name;
    } else if (types.includes('postal_code')) {
      components.postalCode = component.long_name;
    }
  }

  components.streetAddress = [streetNumber, route].filter(Boolean).join(' ') ||
    components.formattedAddress.split(',')[0] || '';

  console.log('Extracted address:', components);
  return components;
}

export default function AddressAutocomplete({
  onPlaceSelect,
  placeholder = 'Start typing an address...',
  className = '',
  error,
  disabled = false,
  initialValue = '',
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Load Google Maps and initialize autocomplete
  useEffect(() => {
    let mounted = true;

    loadGoogleMaps()
      .then(() => {
        if (!mounted || !inputRef.current || autocompleteRef.current) return;

        // Create autocomplete
        autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
          bounds: new google.maps.LatLngBounds(
            { lat: SAUDI_BOUNDS.south, lng: SAUDI_BOUNDS.west },
            { lat: SAUDI_BOUNDS.north, lng: SAUDI_BOUNDS.east }
          ),
          componentRestrictions: { country: 'sa' },
          fields: ['address_components', 'formatted_address', 'geometry', 'name', 'place_id'],
          types: ['geocode', 'establishment'],
        });

        // Handle place selection
        autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current?.getPlace();
          console.log('Place selected:', place);

          if (place?.geometry) {
            const addressData = extractAddressComponents(place);
            onPlaceSelect(addressData);
          }
        });

        setIsReady(true);
      })
      .catch((err: Error) => {
        console.error('Google Maps error:', err);
        if (mounted) setLoadError(err.message);
      });

    return () => {
      mounted = false;
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [onPlaceSelect]);

  const inputClassName = `w-full rounded-lg border px-4 py-2 pr-10 focus:ring-2 focus:ring-[#1B7566]/30 focus:border-[#004D40] focus:outline-none ${
    error ? 'border-red-400' : 'border-gray-300'
  } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''} ${className}`;

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        defaultValue={initialValue}
        placeholder={loadError ? 'Enter address manually...' : placeholder}
        className={inputClassName}
        disabled={disabled}
        autoComplete="off"
      />
      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
        {!isReady && !loadError ? (
          <i className="ri-loader-4-line animate-spin text-gray-400"></i>
        ) : (
          <i className="ri-map-pin-line text-gray-400"></i>
        )}
      </div>
      {loadError && (
        <p className="mt-1 text-xs text-amber-600">
          <i className="ri-alert-line mr-1"></i>
          Autocomplete unavailable - {loadError}
        </p>
      )}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}
